import defaultsDeep from 'lodash/defaultsDeep';

const DEFAULTS = {
  features: {
    metrics: {
      /**
       * Log usage metrics to Google Analytics
       */
      enabled: true
    },
    bestAvailableLink: {
      enabled: false,
      /**
       * If enabled a choose best available button will be shown on both
       * the level-selector screen (if enabled) and in the seat-map controls.
       * The current performance ID will be prepended to the URL.
       *
       * e.g. url: '/booking/production/bestavailable/'
       * => '/booking/production/bestavailable/123'
       */
      url: null,
      labelButton: 'Best Available Seats',
      labelText: 'Choose Best Available Seats',
      labelSelect: 'Or choose Best Available seats'
    },

    /**
     * Display a promo-code form on the level-selector and seat-map
     * WARNING: Only supported with BlocksOffice as the feature uses the
     * built in promo-code routes provided by BlocksOffice rather than proxy calls.
     * {
     *   enabled: true,
     *   endpoints: {
     *    add: '/promocode/add',
     *    remove: '/promocode/remove'
     *   }
     * }
     */
    promoCode: {
      enabled: false,
      endpoints: null,
      defaultSourceNo: null
    },

    /**
     * If enabled renders an additional level-selector screen before the main seat-map.
     * Useful for quick navigation in large venues and to introduce the venue using imagery.
     * Requires graphics to be supplied as part of the facilityMapping config.
     *
     * See the user guide for full implementation details:
     * https://docs.google.com/document/d/17Utl7DcfHBk72HZprvyZC_ib8MtjMbKUNGanhQc_k9Y/edit
     *
     * facilityMapping: [{
     *   ...
     *   imageHighlighted: '/path/to/background.jpg',
     *   imageDefault: cdnUrl + '/assets/wdch-levels-foreground.jpg',
     *   imageMask: cdnUrl + '/assets/wdch-mask.svg',
     *   // What zoom level be used when clicking on a hotspot
     *   zoomLevel: 4,
     *   // Label individual hit-areas
     *   hitAreaLabels: [{
     *     id: 'hit-area-element-id',
     *     label: 'Front Orchestra'
     *   }]
     * }]
     */
    levelSelector: {
      enabled: false,
      bestAvailableUrl: false,
      labelHelp: 'Select your preferred seating area'
    },

    /**
     * If enabled displays a mini-map on the seat-map screen
     * Requires that a `miniMapSvg` is provided in the `facilityMapping`
     */
    miniMap: {
      enabled: false
    },

    /**
     * Pan zoom behaviour.
     * It would be weird to disable this one but the options provided allow
     * you to adjust min/max zoom threshold and zoom sensitivity.
     */
    panZoom: {
      enabled: true,
      options: {
        minZoom: 1,
        maxZoom: 6,
        zoomScaleSensitivity: 1
      }
    },

    /**
     * If enabled allows you to customise the format used for seat descriptions.
     * Accepts a customiser function which passes through the full seat data object
     * for you to use to determine to construct your own set description.
     */
    customSeatDescription: {
      enabled: false,
      customiser: null
    },

    /**
     * Performance details.
     * Allows a web content field ID to be provided to override the standard
     * title returned by GetPerformanceDetailWithDiscountingEx
     */
    details: {
      enabled: true,
      displayTitleWebContentId: null,
      //  e.g. additionalMessageWebContentDescriptions: ['Special Notice']
      additionalMessageWebContentDescriptions: []
    },

    /**
     * If enabled displays a performance switcher in the seat-map controls
     * allowing you to switch between performances within the app.
     */
    performanceSelector: {
      enabled: false
    },

    /**
     * Enables a calls to the custom GetSeatsBriefWithMOS method
     * which allows us to cache the results of the GetSeats* call.
     */
    cacheableGetSeats: {
      enabled: true
    },

    /**
     * If enabled then the application will select the same price type for
     * subsequent seats as long as it has the same hold code, zone, and seat type.
     */
    preferredPriceType: {
      enabled: true
    },

    /**
     * If enabled displays a price key allowing people
     * to filter seats on the seat-map by price.
     */
    priceFilters: {
      enabled: true
    },

    /**
     * If enabled displays a seat key showing icons for
     * each of the available types of seat.
     */
    seatKey: {
      enabled: true
    },

    /**
     * If enabled will display inline view from seat imagery.
     * See the user guide for full implementation details:
     * https://docs.google.com/document/d/17Utl7DcfHBk72HZprvyZC_ib8MtjMbKUNGanhQc_k9Y/edit
     *
     */
    viewFromSeat: {
      enabled: false,
      endpoint: null,
      showCaption: true
    },

    /**
     * If enabled will lock off certain price-types from being processed.
     * Used for editing fixed package performances.
     */
    lockedSeats: {
      enabled: true,
      lockedPriceTypes: []
    },

    /**
     * If enabled will display inline seat-upsells.
     * Assumes a custom SeatUpsells method is implemented
     * which returns a mapping of zones to upsell from/to.
     */
    inlineSeatUpsell: {
      enabled: false
    },

    /**
     * If enabled will show an expiration timer and if the
     * timeout expires will redirect the user to the cart
     */
    expirationTimer: {
      enabled: false,
      timeoutLength: 1.2e6, // 20 minutes
      redirect: ''
    },

    adaAttestation: {
      enabled: false,
      specialRequestOptions: [],
      csiData: {},
      seatTypes: [],
      holdCodes: [],
      smallPrint: null,
    },

    /**
     * Enable pricing colours on the seat map
     */
    seatColours: {
      enabled: false
    },

    /**
     * If enabled, will show a single seat warning
     */
    singleSeatWarning: {
      enabled: false,
      warning: ''
    },

    /**
     * Determine whether web content fields are turned on for
     * showing messages on seat details
     */
    seatWebContentMessages: {
      enabled: false
    },

    /**
     * Enable ticket restrictions based on items in the basket
     */
    ticketRestrictions: {
      enabled: false,
      /**
        restrictions: [{
          // If the cart has the accessible seat
          dependsOn: [8],
          // You can add a companion seat
          restrictedHoldCodes: [13],
          // Display this message to the user on fail
          errorMessage: 'You must add a wheelchair seat to your cart if booking a companion seat'
        }]
       */
      restrictions: []
    }
  },

  /**
   * Set the currency format for the app.
   * Supported values: GBP or USD
   */
  currencyCode: 'USD',

  /**
   * Date format used for performance dates.
   * See: https://date-fns.org/v1.28.5/docs/format
   */
  dateTimeFormat: 'EEE, MMMM d, yyyy, h:mmaaa',

  /**
   * Configure fallback behaviour in case of an error.
   * fallbackUrl is where to send people in case of an error
   * defaultMessage is the message shown as an alert if the app errors out
   * unsupportedMessages is shown if the user-agent doesn't support SVG
   */
  fallbacks: {
    fallbackUrl: '/Cart/Details',
    defaultMessage: 'There was an error loading select your own seat.',
    unsupportedMessage: 'Your browser does not support select your own seat'
  },

  /**
   * Set timezone
   */

  timezone: 'Australia/Sydney',

  /**
   * Message shown when reaching the maximum selected seats limit.
   * This is typically around 10 but is determined by a call to GetMaxTicketAmount.
   */
  maxTicketsMessage: 'You cannot select any more seats.',

  /**
   * Configures seat-map and level-selector graphics for individual facilities
   * (optional, but required if using a custom seat-map graphic)
   */
  facilityMapping: [],

  basket: {
    label: 'Cart',
    totalLabel: 'Total',
    submitLabel: 'Add to cart',
    successLabel: 'Success',
    updateLabel: 'Update cart',
    processingLabel: 'Processing',
    existingMessage: 'This seat is in your cart',
    extraMessages: [
      'Seats are not reserved until they have been added to your cart.'
    ]
  },

  /**
   * Configurable seat options for the booking process
   */
  specialRequests: {
    /**
     * Config option to leave single seats, option can be Y or N
     */
    LeaveSingleSeats: 'Y'
  },

  seatTypes: {
    /**
     * Default types, defines config for seat types
     * Available options:
     *
     *
     * label - Label used in the key
     * id: - Unique ID used to identify the icon
     * slug - Used for CSS class applied to the seat
     * method - Either 'default' or 'custom'
     * customName - If method is 'custom' defines which icon to use, must match layer name
     * holdCodes - Array of hold code id's icon will be used for matching seats
     * seatTypeIds - Array of seat status id's icon will be used for matching seats
     */
    defaultTypes: [
      {
        label: 'Available',
        id: 'regularSeat',
        slug: 'available',
        method: 'default',
        customName: null,
        holdCodes: [],
        seatTypeIds: []
      },
      {
        label: 'Selected',
        id: 'selectedSeat',
        slug: 'selected',
        method: 'default',
        customName: null,
        holdCodes: [],
        seatTypeIds: []
      },
      {
        label: 'Not Available',
        id: 'unavailableSeat',
        slug: 'unavailable',
        method: 'default',
        customName: null,
        holdCodes: [],
        seatTypeIds: []
      }
    ],

    /**
     * Used to define custom seat icons per client, for example:
     * customTypes: [{
     *   // Label shown in the seat-key
     *   label: 'ADA',
     *   // Short ID added to the element for styling
     *   slug: 'accessible',
     *   // Method must always be 'custom'
     *   method: 'custom',
     *   // Needs to match the layer name of the icon in your Illustrator file
     *   customName: 'custom-icon-ada',
     *   // Array of hold-code IDs to match against
     *   holdCodes: [36],
     *   // Array of seat-type IDs to match against
     *   seatTypeIds: [8]
     * }]
     */
    customTypes: [],

    /**
     * Status short-codes to determine hidden/blacked-out seats.
     * BLK / blacked-out is the default.
     */
    hiddenStatusCodes: ['BLK'],

    /**
     * Status hold-codes to determine hidden/blacked-out seats.
     */
     hiddenHoldCodes: [],

    /**
     * Determines which Tessitura status codes match to what status of seat.
     * defaultMapping: defines that if in doubt consider seats unavailable.
     * seatMappings: defines mappings between status codes and how the seat is shown.
     * - AVL = Available
     * - HBR = Breakable hold-code
     * - TKD = Ticketed
     * - HLD = Held
     */
    defaultMapping: { status: 'unavailable', draw: 'unavailableSeat' },
    seatMappings: [
      { statusCode: 'AVL', status: 'available', draw: 'regularSeat' },
      { statusCode: 'HBR', status: 'available', draw: 'regularSeat' },
      { statusCode: 'TKD', status: 'unavailable', draw: 'unavailableSeat' },
      { statusCode: 'HLD', status: 'unavailable', draw: 'unavailableSeat' },
      { statusCode: 'RUP', status: 'unavailable', draw: 'unavailableSeat' }
    ]
  },

  /**
   * Custom seat messages
   * Array of possible messages to show for a seat,
   * messages can be associated with one or more seat type or hold codes
   *
   * Example format:
   * {
   *     text: 'My message',
   *     seatTypeIds: [1, 2],
   *     holdCodeIds: []
   * }
   */
  seatMessages: [],

  priceTypeMessages: [],

  zoneMessages: [],

  /**
   * Show performance type under the title
   * Performance type description from Tessitura can be overridden
   * using the customDescription property by providing the Tessitura ID of the
   * performance type along with description override
   *
   * Example format:
   * {
   *   3: 'Close Captioned Performance'
   * }
   */
   performanceType: {
     enabled: false,
     customDescription: {},
   },

  /**
   * The mode in which we're interacting with Tessitura
   * This can be either REST or SOAP, and will determine
   * which one we use in the API.js main file
   */
  api_mode: 'REST',
};

export default class Config {
  getConfig() {
    if (window.SYOSConfig) {
      if (!window.SYOSParsedConfig) {
        window.SYOSParsedConfig = defaultsDeep(
          window.SYOSConfig || {},
          DEFAULTS
        );
      }
      return window.SYOSParsedConfig;
    } else {
      return DEFAULTS;
    }
  }
}



// WEBPACK FOOTER //
// ./src/javascripts/config.js